import typography from '@amblique/king-living/app/theme/foundations/typography';
import space from '@amblique/king-living/app/theme/foundations/space';

const {textStyles} = typography;
const optionalAddOnsSectionSpacing = {
  base: space[14],
  lg: '48px',
};

const OptionalAddOns = {
  parts: [
    'root',
    'header',
    'title',
    'body',
    'group',
    // Group header (thumbnail + typeName title + shortDescription)
    'groupHeader',
    'groupHeaderImage',
    'groupHeaderInfo',
    'groupTitle',
    'groupDescription',
    // Item row
    'item',
    'itemImageWrap',
    'itemImage',
    'itemCheckbox',
    'itemLabel',
    'itemMeta',
    'itemPrice',
    'detailsLink'
  ],
  baseStyle: {
    root: {
      width: '100%',
      paddingTop: {base: 14,lg: '64px'},
      marginTop: {base: 10, lg: 12},
      paddingBottom: optionalAddOnsSectionSpacing,
      borderTopWidth: '1px',
      borderTopColor: 'gray.200',
    },
    header: {
      marginBottom: 4,
    },
    title: {
      color: 'brandColors.greyscale',
      fontVariantNumeric: 'lining-nums proportional-nums',
      fontFamily: 'body',
      fontSize: {base: 'md', lg: 'lg'},
      fontStyle: 'normal',
      fontWeight: 'medium',
      lineHeight: {base: 'base', lg: 'shorter'},
    },
    body: {
      display: 'flex',
      flexDirection: 'column',
      gap: {base: 4, lg: 6},
    },
    group: {
      alignSelf: 'stretch',
      bg: 'white',
      borderRadius: 'base',
      overflow: 'hidden',
    },
    // Header section within each group card
    groupHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 4,
      marginBottom: 0,
      padding: `${space[5]} ${space[6]}`,
      width: '100%',
      borderWidth: 0,
    },
    groupHeaderImage: {
      flexShrink: 0,
      width: 15,
      height: 15,
      objectFit: 'cover',
      borderRadius: 'md',
    },
    groupHeaderInfo: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      alignSelf: 'stretch',
      gap: 2,
      borderWidth: 0,
      borderStyle: 'none',
      '& > *': {
        marginBottom: 0,
      },
    },
    groupTitle: {
      ...textStyles.labelDefault,
      color: 'brandColors.greyscale90',
      fontVariantNumeric: 'lining-nums proportional-nums',
    },
    groupDescription: {
      ...textStyles.bodyS,
      color: 'brandColors.greyscale50',
      fontVariantNumeric: 'lining-nums proportional-nums',
    },
    item: {
      display: 'flex',
      alignItems: 'center',
      alignSelf: 'stretch',
      gap: 4,
      paddingTop: 3.5,
      paddingRight: 5,
      paddingBottom: 3.5,
      paddingLeft: 6,
      borderTopWidth: '1px',
      borderTopStyle: 'solid',
      borderTopColor: 'brandColors.greyscale10',
    },
    itemImageWrap: {
      flexShrink: 0,
      borderRadius: '6px',
      h: '64px',
      overflow: 'hidden',
      w: '64px',
      sx: {
        '&:empty': {
          bg: 'brandColors.greyscale10',
        },
      },
    },
    itemImage: {
      borderRadius: 'sm',
      h: '100%',
      objectFit: 'cover',
      w: '100%',
    },
    itemMeta: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
    },
    itemLabel: {
      color: 'brandColors.greyscale90',
      cursor: 'pointer',
      fontFamily: 'body',
      fontSize: '0.875rem',
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: 1.4,
      fontVariantNumeric: 'lining-nums proportional-nums',
    },
    itemPrice: {
      color: 'brandColors.greyscale70',
      flexShrink: 0,
      fontFamily: 'body',
      fontSize: '0.8125rem',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: 1.2,
      fontVariantNumeric: 'lining-nums proportional-nums',
      textAlign: 'center',
    },
    itemCheckbox: {
      flexShrink: 0,
      sx: {
        '& .chakra-checkbox__control': {
          w: 7.5,
          h: 7.5,
          minW: 7.5,
          minH: 7.5,
          borderRadius: '5.625px',
          borderWidth: '1.406px',
          borderStyle: 'solid',
          borderColor: 'brandColors.linen03',
          backgroundColor: 'text.white',
          boxShadow: 'none',
          _hover: {
            borderColor: 'brandColors.greyscale40',
          },
          _focus: {
            boxShadow: 'none',
          },
          _focusVisible: {
            boxShadow: '0 0 0 2px rgba(27, 26, 24, 0.25)',
          },
        },
        '& .chakra-checkbox__control[data-checked]': {
          borderWidth: '1.406px',
          backgroundColor: 'brandColors.greyscale',
          borderColor: 'brandColors.greyscale',
          color: 'white',
          _hover: {
            backgroundColor: 'brandColors.greyscale',
            borderColor: 'brandColors.greyscale',
          },
          _focus: {
            boxShadow: 'none',
          },
          _focusVisible: {
            boxShadow: '0 0 0 2px rgba(27, 26, 24, 0.45)',
          },
        },
        '& .chakra-checkbox__control[data-checked] svg': {
          color: 'white',
        },
        '& .chakra-checkbox__control[data-disabled]': {
          borderRadius: '5.625px',
          borderWidth: '1.406px',
          borderStyle: 'solid',
          borderColor: 'greyscale.30',
          backgroundColor: 'brandColors.greyscale10',
        },
        '& .chakra-checkbox__control[data-checked][data-disabled]': {
          borderRadius: '5.625px',
          borderWidth: '1.406px',
          borderStyle: 'solid',
          borderColor: 'greyscale.30',
          backgroundColor: 'brandColors.greyscale50',
        },
      },
    },
    detailsLink: {
      alignSelf: 'flex-start',
      background: 'none',
      border: 'none',
      color: 'brandColors.viewDetailsLink',
      cursor: 'pointer',
      display: 'inline',
      fontFamily: 'body',
      fontSize: '0.8125rem',
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: 1.2,
      fontVariantNumeric: 'lining-nums proportional-nums',
      padding: 0,
      textAlign: 'left',
      textDecoration: 'none',
      _hover: {
        color: 'brandColors.viewDetailsLink',
      },
    },
  },
};

export default OptionalAddOns;
